import { ReactNode, useState, useEffect } from 'react';
import config from 'config';
import { useReduxState } from 'hooks';
import { getLocalizedBlock } from 'shared-helpers/courses';
import { LoadingIndicator } from 'components/common';
import { FormGroup } from 'components/common/form';
import { EditorBlockEmbedIframe, EditorBlockEmbedControls } from '.';

import type {
  Block,
  BlockEmbed,
  BlockH5P,
  BlockScorm,
  Course
} from 'shared-types';
import type { BlockEmbedStateProps } from './EditorBlockEmbed';

interface Props extends BlockEmbedStateProps {
  blockId: Block['id'];
  blockType: 'embedcode' | 'embedlink' | 'h5p' | 'scorm';
  customControls?: ReactNode;
  data: BlockEmbed | BlockH5P | BlockScorm;
  dataFromDefaultLanguage?: boolean;
  disableIsMandatorySettings?: boolean;
  hideNonTranslatableSettings?: boolean;
  isPreview: boolean;
  languageVersion: Course['language'];
  loading: boolean;
  style?: React.CSSProperties;
  updateData: (data: BlockEmbed | BlockH5P | BlockScorm) => void;
  updateOnChange?: string;
}

const EditorBlockEmbedPreview = ({
  blockId,
  blockType,
  customControls,
  data,
  dataFromDefaultLanguage = false,
  disableIsMandatorySettings,
  hideNonTranslatableSettings,
  isPreview,
  languageVersion,
  loading,
  updateData,
  updateOnChange,
  style,
  ...stateProps
}: Props) => {
  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const [iframeKey, setIframeKey] = useState<number>(Date.now());
  const [editorIframeWidth, setEditorIframeWidth] = useState<number>(undefined);

  useEffect(() => {
    if (updateOnChange) {
      const timer = setTimeout(() => setIframeKey(Date.now()), 0);
      return () => clearTimeout(timer);
    }
  }, [updateOnChange]);

  const handleSetIframeWidth = (width: number) => setEditorIframeWidth(width);

  const getEmbedUrl = (): string => {
    const endpointType =
      blockType === 'scorm'
        ? 'embed-block-scorm'
        : blockType === 'h5p'
        ? 'embed-block-h5p'
        : 'embed-block-html';

    return (
      `${config.apiUrl}/reader/courses/${course.slug}/${endpointType}/${blockId}` +
      `?preview=true` +
      `&language=${dataFromDefaultLanguage ? course.language : languageVersion}`
    );
  };

  const localizedData = getLocalizedBlock({
    block: { data, type: blockType },
    languageDefault: course.language,
    languageTarget: languageVersion
  });

  return loading ? (
    <LoadingIndicator />
  ) : (
    <div className="embed" style={style}>
      <FormGroup
        label={isPreview ? null : messages.commonPreview}
        maxWidth="100%"
      >
        <div className="preview">
          <EditorBlockEmbedIframe
            iframeKey={iframeKey.toString()}
            blockId={blockId}
            data={localizedData.data}
            isScorm={blockType === 'scorm'}
            src={getEmbedUrl()}
            setIframeWidth={handleSetIframeWidth}
          />
        </div>
      </FormGroup>

      <EditorBlockEmbedControls
        blockId={blockId}
        blockType={blockType}
        customControls={customControls}
        data={data}
        disableIsMandatorySettings={disableIsMandatorySettings}
        isPreview={isPreview}
        hideNonTranslatableSettings={hideNonTranslatableSettings}
        languageVersion={languageVersion}
        iframeWidth={editorIframeWidth}
        updateData={updateData}
        {...stateProps}
      />

      <style jsx>{`
        .preview {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockEmbedPreview;
